if (import.meta.hot) {
  import.meta.hot.accept();
}

import { upgradeCheckboxOptions } from './lib/checkbox-option.js';

(function () {
  'use strict';

  // ── Filter Overlay ────────────────────────────────────────────────────────────
  //
  // The one overlay implementation of the project: the event list's category
  // filter, the Solr result page's facet overlay and the news/story overview's
  // filter bar all open through it (`[data-filter-toggle]` +
  // `aria-controls="<overlay id>"` + `[data-filter-close]`).
  //
  // Two flavours of overlay are supported, told apart by the markup itself:
  //
  // - Filter bars that stay in the layout on desktop (inline dropdowns) and only
  //   become a full-screen panel on mobile. They collapse via CSS, so they rely
  //   on the `is-open` class alone.
  // - Overlays that are `hidden` until opened (Solr result page). Those get the
  //   attribute removed on open and re-applied once the closing transition ended.
  //
  // On the news/story overview and the Solr result page the whole filter chrome
  // lives inside the region filter-ajax.js swaps, so `init()` runs again on
  // `altana:dom-updated`. Everything below is therefore written to be
  // re-runnable: the toggle and the overlay carry their own `*-ready` guard (they
  // can in principle be replaced independently), the close buttons are handled by
  // delegation on the overlay so replacing the panel body keeps working, and the
  // document-level handlers are registered once at module level instead of once
  // per element.
  //
  // That swap also destroys an *open* panel and re-renders it closed, which is
  // why the open state is remembered per overlay id at module level and re-applied
  // by `restoreOpen()` during the re-init — not by re-running `openOverlay()`,
  // whose slide-in animation would make the panel look like it left the viewport
  // and came back on every filter change.

  const FOCUSABLE =
    'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  const TOGGLE_READY = 'data-filter-toggle-ready';
  const OVERLAY_READY = 'data-filter-overlay-ready';
  const DROPDOWN_READY = 'data-facet-dropdown-ready';
  // Remembers that the overlay was `hidden` when the server rendered it, which
  // open() has to undo and close() has to re-apply. Read from a data attribute
  // rather than a closure so a re-init after a swap reaches the same conclusion.
  const OVERLAY_HIDDEN = 'data-filter-overlay-uses-hidden';

  let previouslyFocused = null;

  // Ids of the overlays that are open right now. Keyed by id rather than by
  // element on purpose: the node this refers to is replaced wholesale on every
  // filter change, and the id is the only thing that survives it.
  const openOverlayIds = new Set();

  function overlayOf(toggleBtn) {
    const overlayId = toggleBtn.getAttribute('aria-controls');
    return overlayId ? document.getElementById(overlayId) : null;
  }

  function togglesFor(overlay) {
    return Array.from(document.querySelectorAll(`[data-filter-toggle][aria-controls="${overlay.id}"]`));
  }

  // Dialog semantics apply only while the overlay is actually a panel (i.e.
  // open, on mobile); on desktop it is a plain inline container. Overlays
  // carrying a static `role` keep whatever they declared themselves.
  function managesDialogRole(overlay) {
    return overlay.hasAttribute('data-filter-dialog') && !overlay.hasAttribute('data-filter-static-role');
  }

  function openOverlay(toggleBtn, overlay) {
    previouslyFocused = document.activeElement;

    if (overlay.hasAttribute(OVERLAY_HIDDEN)) {
      overlay.removeAttribute('hidden');
      // Commit the closed state (still `display: none` a moment ago) before
      // `is-open` flips it, otherwise the slide-in has no start value to
      // animate from and the panel just appears.
      void overlay.offsetWidth;
    }
    if (managesDialogRole(overlay)) {
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
    }
    overlay.classList.add('is-open');
    openOverlayIds.add(overlay.id);
    toggleBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    overlay.querySelector(FOCUSABLE)?.focus();
  }

  // Re-applies the state of a panel that was open when filter-ajax.js replaced
  // its markup. Deliberately not `openOverlay()`:
  //
  // - no animation. The panel was already on screen before the swap, so it must
  //   land in its open position without transitioning there. Suppressing the
  //   transition while `is-open` is committed covers both possible starting
  //   points — a fresh node whose closed style was never flushed (no transition
  //   to begin with) and one another `altana:dom-updated` listener already forced
  //   a layout on (closed style committed, so `is-open` would animate).
  // - no focus move. filter-ajax.js restores focus to the control the user
  //   actually activated right after this, and stealing it here would fight that.
  function restoreOpen(overlay) {
    if (overlay.hasAttribute(OVERLAY_HIDDEN)) {
      overlay.removeAttribute('hidden');
    }
    if (managesDialogRole(overlay)) {
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
    }

    overlay.style.transition = 'none';
    overlay.classList.add('is-open');
    void overlay.offsetWidth;
    overlay.style.transition = '';

    togglesFor(overlay).forEach((toggleBtn) => toggleBtn.setAttribute('aria-expanded', 'true'));
    document.body.style.overflow = 'hidden';
  }

  function closeOverlay(overlay) {
    overlay.classList.remove('is-open');
    openOverlayIds.delete(overlay.id);
    togglesFor(overlay).forEach((toggleBtn) => toggleBtn.setAttribute('aria-expanded', 'false'));
    document.body.style.overflow = '';

    if (managesDialogRole(overlay)) {
      overlay.removeAttribute('role');
      overlay.removeAttribute('aria-modal');
    }

    // Wait for the slide-out transition before taking the panel out of the
    // layout again, otherwise it would vanish instead of animating away.
    if (overlay.hasAttribute(OVERLAY_HIDDEN)) {
      overlay.addEventListener(
        'transitionend',
        function onEnd() {
          overlay.setAttribute('hidden', '');
        },
        { once: true }
      );
    }

    // The element focus came from may have been swapped away while the panel was
    // open (a category option, a chip that no longer exists), in which case
    // focusing it would drop focus to <body>. The toggle is where a dialog is
    // expected to hand focus back anyway.
    const returnTarget = previouslyFocused?.isConnected ? previouslyFocused : togglesFor(overlay)[0];
    returnTarget?.focus();
  }

  function initFilterToggle(toggleBtn) {
    if (toggleBtn.hasAttribute(TOGGLE_READY) || !overlayOf(toggleBtn)) {
      return;
    }
    toggleBtn.setAttribute(TOGGLE_READY, '');

    // Resolved per click, not captured: a swap may have replaced the panel with
    // a fresh element carrying the same id.
    toggleBtn.addEventListener('click', () => {
      const overlay = overlayOf(toggleBtn);
      if (!overlay) {
        return;
      }
      if (overlay.classList.contains('is-open')) {
        closeOverlay(overlay);
      } else {
        openOverlay(toggleBtn, overlay);
      }
    });
  }

  function initOverlay(overlay) {
    if (overlay.hasAttribute(OVERLAY_READY)) {
      return;
    }
    overlay.setAttribute(OVERLAY_READY, '');

    if (overlay.hasAttribute('hidden')) {
      overlay.setAttribute(OVERLAY_HIDDEN, '');
    }
    if (overlay.hasAttribute('role')) {
      overlay.setAttribute('data-filter-static-role', '');
    }

    // Delegated: the buttons live in the panel body, which filter-ajax.js
    // replaces on every filter change.
    overlay.addEventListener('click', (event) => {
      if (event.target.closest('[data-filter-close]')) {
        closeOverlay(overlay);
      }
    });

    // Close on Escape
    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeOverlay(overlay);
      }
    });

    // Focus trap (only meaningful while the dialog role is active)
    overlay.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !overlay.hasAttribute('role')) {
        return;
      }

      const focusable = Array.from(overlay.querySelectorAll(FOCUSABLE)).filter(
        (el) => !el.closest('[hidden]') || el.closest('[hidden]') === overlay
      );

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    });

    // This node is the replacement of a panel that was open: bring it back open
    // here, while the fresh (closed) markup has not been painted yet. Doing it in
    // the `altana:dom-updated` re-init rather than leaving it to filter-ajax.js
    // also keeps that module's generic "click the toggle again" restore from
    // running for this overlay — it skips toggles that already report expanded.
    if (openOverlayIds.has(overlay.id)) {
      restoreOpen(overlay);
    }
  }

  // ── Facet Dropdowns ───────────────────────────────────────────────────────────

  function dropdownParts(container) {
    return {
      toggle: container.querySelector('.facet-dropdown__toggle'),
      panel: container.querySelector('.facet-dropdown__panel'),
    };
  }

  function isDropdownOpen(container) {
    return dropdownParts(container).toggle?.getAttribute('aria-expanded') === 'true';
  }

  function closeDropdown(container) {
    const { toggle, panel } = dropdownParts(container);
    toggle?.setAttribute('aria-expanded', 'false');
    panel?.setAttribute('hidden', '');
  }

  function openDropdown(container) {
    // Accordion behaviour: only one facet dropdown open at a time, so opening
    // this one closes any sibling dropdown left open in the same filter bar.
    // Scoped by a DOM query rather than a list of closures, so re-initialising
    // after a swap cannot accumulate references to detached containers.
    const scope = container.closest('.category-filter') || container.closest('[data-filter-ajax]') || document;
    scope.querySelectorAll('[data-facet-dropdown]').forEach((other) => {
      if (other !== container && isDropdownOpen(other)) {
        closeDropdown(other);
      }
    });

    const { toggle, panel } = dropdownParts(container);
    toggle?.setAttribute('aria-expanded', 'true');
    panel?.removeAttribute('hidden');
  }

  function initFacetDropdown(container) {
    if (container.hasAttribute(DROPDOWN_READY)) {
      return;
    }
    const { toggle, panel } = dropdownParts(container);
    if (!toggle || !panel) {
      return;
    }
    container.setAttribute(DROPDOWN_READY, '');

    // Solr's facet options ship as links (bookmarkable, no-JS) and become real
    // checkbox controls now that there is JavaScript to give them the keyboard
    // contract. The event list's rows share these classes but are <label>s
    // around native checkboxes, which the upgrade leaves alone.
    upgradeCheckboxOptions(panel, '.facet-option-item__checkbox-wrap');

    toggle.addEventListener('click', () => {
      if (isDropdownOpen(container)) {
        closeDropdown(container);
      } else {
        openDropdown(container);
      }
    });

    // Tabbing (or Shift+Tabbing) focus out of the dropdown entirely — e.g. onto
    // the next facet dropdown's toggle — should close it; focus moving between
    // the toggle and its own panel must not. This is scoped to the Tab key
    // specifically (not a generic focusout/blur check): mouse-driven focus is
    // unreliable across browsers (Safari doesn't focus a <button> on click at
    // all) and environments, so a blur-based close would fire right after a
    // checkbox click and hide the panel it was just opened to show. Real Tab
    // key presses reliably move document.activeElement, so checking it just
    // after the browser processes the keypress (deferred one tick) is safe.
    function handleTabKey(event) {
      if (event.key === 'Escape') {
        closeDropdown(container);
        toggle.focus();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      window.setTimeout(() => {
        if (isDropdownOpen(container) && !container.contains(document.activeElement)) {
          closeDropdown(container);
        }
      }, 0);
    }

    toggle.addEventListener('keydown', handleTabKey);
    panel.addEventListener('keydown', handleTabKey);
  }

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-facet-dropdown]').forEach((container) => {
      if (isDropdownOpen(container) && !container.contains(event.target)) {
        closeDropdown(container);
      }
    });
  });

  function init() {
    document.querySelectorAll('[data-filter-toggle]').forEach((toggleBtn) => {
      const overlay = overlayOf(toggleBtn);
      if (overlay) {
        initOverlay(overlay);
        initFilterToggle(toggleBtn);
      }
    });

    document.querySelectorAll('[data-facet-dropdown]').forEach(initFacetDropdown);
  }

  document.addEventListener('altana:dom-updated', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
